import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc, serverTimestamp, deleteField,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Box, Typography, Paper, Button, TextField, Alert, CircularProgress, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Checkbox, FormControlLabel, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButton, ToggleButtonGroup, InputAdornment,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import Layout from '../components/Layout';
import SignaturePad from '../components/SignaturePad';
import SchoolContractDocument from '../components/SchoolContractDocument';
import { logError } from '../utils/errorLogger';

// /admin/school-contracts — the high-school season court rental agreements.
// Fill in the form, preview the agreement as the school will read it, then send
// it: each school signer gets their own link, signs without an account, and the
// director countersigns here. Schools prepay by check, recorded here. The wording lives in functions/schoolContracts.js.

const navy = '#1E3A5F';
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS = {
  draft: { label: 'Draft', color: 'default' },
  sent: { label: 'Out for signature', color: 'warning' },
  signed: { label: 'Signed', color: 'success' },
  void: { label: 'Withdrawn', color: 'default' },
};
const PAYMENT = {
  unpaid: { label: 'Unpaid', color: 'error' },
  partial: { label: 'Part paid', color: 'warning' },
  paid: { label: 'Paid', color: 'success' },
};

const FALLBACK_DEFAULTS = {
  days: [1, 2, 3, 4], windowStart: '14:30', windowEnd: '17:30', courts: 2, fee: 5000,
  preseasonPrice: 500, districtPrice: 500, regionalPrice: 500,
  legalName: 'Torch Volleyball, Inc.', directorName: 'Steven Long', directorTitle: 'Director, Torch Volleyball',
  checkAddress: '',
};

const fmtMoney = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const totalCentsOf = (c) => Math.round(((Number(c.fee) || 0)
  + (c.addOns?.preseason?.selected ? Number(c.addOns.preseason.price) || 0 : 0)
  + (c.addOns?.district?.selected ? Number(c.addOns.district.price) || 0 : 0)
  + (c.addOns?.regional?.selected ? Number(c.addOns.regional.price) || 0 : 0)) * 100);
const shortDate = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function blankContract(d) {
  return {
    schoolName: '', schoolAddress: '', schoolPhone: '', sport: 'Girls Beach Volleyball',
    signers: [{ name: '', title: 'Principal', email: '' }, { name: '', title: 'Athletic Director', email: '' }],
    seasonStart: '', seasonEnd: '', days: d.days, windowStart: d.windowStart, windowEnd: d.windowEnd,
    courts: d.courts, fee: d.fee,
    addOns: {
      preseason: { selected: false, start: '', end: '', price: d.preseasonPrice },
      district: { selected: false, price: d.districtPrice },
      regional: { selected: false, price: d.regionalPrice },
    },
    legalName: d.legalName, checkAddress: d.checkAddress, notes: '',
  };
}

// The fields the form edits — everything else on the record (status, seal,
// signatures, payment) is the server's.
const FORM_KEYS = ['schoolName', 'schoolAddress', 'schoolPhone', 'sport', 'signers', 'seasonStart', 'seasonEnd',
  'days', 'windowStart', 'windowEnd', 'courts', 'fee', 'addOns', 'legalName', 'checkAddress', 'notes'];
const pickForm = (c) => Object.fromEntries(FORM_KEYS.map(k => [k, c[k]]));

export default function AdminSchoolContracts({ embedded = false }) {
  // Inside the Schools page tabs the page around it supplies the Layout.
  const Wrap = embedded ? React.Fragment : Layout;
  const narrow = useMediaQuery('(max-width:900px)');
  const [contracts, setContracts] = useState([]);
  const [defaults, setDefaults] = useState(FALLBACK_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editor, setEditor] = useState(null); // { id|null, form }
  const [preview, setPreview] = useState({ html: '', problems: [], totalCents: 0 });
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);

  const [detailId, setDetailId] = useState(null);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultsForm, setDefaultsForm] = useState(FALLBACK_DEFAULTS);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'schoolContracts'))
      .then(s => { if (s.exists()) setDefaults({ ...FALLBACK_DEFAULTS, ...s.data() }); })
      .catch(() => {});
    const unsub = onSnapshot(collection(db, 'schoolContracts'), snap => {
      setContracts(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.seasonStart || '').localeCompare(a.seasonStart || '') || (a.schoolName || '').localeCompare(b.schoolName || '')));
      setLoading(false);
    }, err => {
      setError(err.code === 'permission-denied' ? 'Admins only.' : 'Could not load contracts.');
      setLoading(false);
    });
    return unsub;
  }, []);

  // Preview follows the form, half a second after typing stops.
  const previewTimer = useRef(null);
  useEffect(() => {
    if (!editor) return undefined;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await httpsCallable(getFunctions(), 'previewSchoolContract')({ contract: editor.form });
        setPreview(res.data);
      } catch (e) {
        setPreview(p => ({ ...p, problems: [e.message] }));
      }
      setPreviewing(false);
    }, 500);
    return () => clearTimeout(previewTimer.current);
  }, [editor]);

  const detail = contracts.find(c => c.id === detailId) || null;

  const setForm = (patch) => setEditor(ed => ({ ...ed, form: { ...ed.form, ...patch } }));
  const setAddOn = (key, patch) => setEditor(ed => ({
    ...ed, form: { ...ed.form, addOns: { ...ed.form.addOns, [key]: { ...ed.form.addOns[key], ...patch } } },
  }));
  const setSigner = (i, patch) => setEditor(ed => ({
    ...ed, form: { ...ed.form, signers: ed.form.signers.map((s, j) => (j === i ? { ...s, ...patch } : s)) },
  }));

  const saveDraft = async () => {
    const form = editor.form;
    if (editor.id) {
      await updateDoc(doc(db, 'schoolContracts', editor.id), { ...form, updatedAt: serverTimestamp() });
      return editor.id;
    }
    const ref = await addDoc(collection(db, 'schoolContracts'), {
      ...form, status: 'draft', payment: { status: 'unpaid' }, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    setEditor(ed => ({ ...ed, id: ref.id }));
    return ref.id;
  };

  const handleSave = async () => {
    setBusy(true);
    setError('');
    try {
      await saveDraft();
      setSuccess('Draft saved.');
      setEditor(null);
    } catch (e) {
      logError('AdminSchoolContracts.save', e.message, e.stack || '');
      setError(`Save failed: ${e.message}`);
    }
    setBusy(false);
  };

  const handleSend = async (email) => {
    setBusy(true);
    setError('');
    try {
      const id = await saveDraft();
      const res = await httpsCallable(getFunctions(), 'sendSchoolContract')({ contractId: id, email });
      setSuccess(email
        ? `Sent to ${res.data.emailed.join(', ') || 'no one — check the signer emails'}.`
        : 'Signing links are ready — copy them from the contract.');
      setEditor(null);
      setDetailId(id);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const openNew = () => {
    setPreview({ html: '', problems: [], totalCents: 0 });
    setEditor({ id: null, form: blankContract(defaults) });
  };
  const openEdit = (c) => {
    setDetailId(null);
    setPreview({ html: '', problems: [], totalCents: 0 });
    setEditor({ id: c.id, form: { ...blankContract(defaults), ...pickForm(c) } });
  };
  // Next season's contract for the same school: everything but the dates.
  const duplicate = (c) => {
    setDetailId(null);
    const form = { ...blankContract(defaults), ...pickForm(c), seasonStart: '', seasonEnd: '' };
    form.addOns = { ...form.addOns, preseason: { ...form.addOns.preseason, start: '', end: '' } };
    form.signers = (form.signers || []).map(s => ({ name: s.name, title: s.title, email: s.email }));
    setPreview({ html: '', problems: [], totalCents: 0 });
    setEditor({ id: null, form });
  };

  const saveDefaults = async () => {
    try {
      const clean = {
        ...defaultsForm,
        fee: Number(defaultsForm.fee) || 0, courts: Number(defaultsForm.courts) || 2,
        preseasonPrice: Number(defaultsForm.preseasonPrice) || 0,
        districtPrice: Number(defaultsForm.districtPrice) || 0,
        regionalPrice: Number(defaultsForm.regionalPrice) || 0,
      };
      await setDoc(doc(db, 'settings', 'schoolContracts'), clean, { merge: true });
      setDefaults(clean);
      setDefaultsOpen(false);
      setSuccess('Defaults saved. New contracts start from them.');
    } catch (e) {
      setError(`Could not save defaults: ${e.message}`);
    }
  };

  if (loading) return <Wrap><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Wrap>;

  return (
    <Wrap>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <SchoolIcon sx={{ color: navy, fontSize: 32 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: navy }}>School Contracts</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              High-school season court rentals · signed online · prepaid
            </Typography>
          </Box>
          <Button startIcon={<SettingsIcon />} onClick={() => { setDefaultsForm(defaults); setDefaultsOpen(true); }}>
            Defaults
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
            sx={{ backgroundColor: navy, '&:hover': { backgroundColor: '#2E6DA4' } }}>
            New contract
          </Button>
        </Box>

        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
        {error && !editor && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {!defaults.checkAddress && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Set the check mailing address under <strong>Defaults</strong> — contracts can’t be sent without it.
          </Alert>
        )}

        <Paper elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
          {contracts.length === 0 ? (
            <Typography sx={{ p: 4, color: '#aaa', textAlign: 'center' }}>No contracts yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>School</TableCell>
                  <TableCell>Season</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map(c => {
                  const st = STATUS[c.status] || STATUS.draft;
                  const pay = PAYMENT[c.payment?.status || 'unpaid'];
                  const signedCount = (c.signers || []).filter(s => s.signedAt).length + (c.director?.signedAt ? 1 : 0);
                  const signerCount = (c.signers || []).filter(s => s.name).length + 1;
                  return (
                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => (c.status === 'draft' ? openEdit(c) : setDetailId(c.id))}>
                      <TableCell sx={{ fontWeight: 600 }}>{c.schoolName || '(unnamed)'}</TableCell>
                      <TableCell>{shortDate(c.seasonStart)} – {shortDate(c.seasonEnd)}</TableCell>
                      <TableCell align="right">{fmtMoney(totalCentsOf(c))}</TableCell>
                      <TableCell>
                        <Chip size="small" color={st.color} label={c.status === 'sent' ? `${st.label} · ${signedCount}/${signerCount}` : st.label} />
                      </TableCell>
                      <TableCell><Chip size="small" variant="outlined" color={pay.color} label={pay.label} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      {editor && (
        <Dialog open fullScreen={narrow} maxWidth="xl" fullWidth onClose={() => !busy && setEditor(null)}>
          <DialogTitle>{editor.id ? 'Edit contract' : 'New school contract'}</DialogTitle>
          <DialogContent dividers>
            {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 5 }}>
                <EditorForm form={editor.form} setForm={setForm} setAddOn={setAddOn} setSigner={setSigner} />
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Preview</Typography>
                  {previewing && <CircularProgress size={14} />}
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>Total {fmtMoney(preview.totalCents || 0)}</Typography>
                </Box>
                {preview.problems?.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Before sending: {preview.problems.join(' ')}
                  </Alert>
                )}
                <Box sx={{ border: '1px solid #ddd', borderRadius: 1, maxHeight: narrow ? 'none' : '70vh', overflowY: 'auto' }}>
                  <SchoolContractDocument html={preview.html}
                    signers={editor.form.signers.filter(s => s.name).map(s => ({ name: s.name, title: s.title }))}
                    director={{ name: defaults.directorName, title: defaults.directorTitle }}
                    schoolName={editor.form.schoolName} />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={() => setEditor(null)} disabled={busy}>Cancel</Button>
            <Button onClick={handleSave} disabled={busy}>Save draft</Button>
            <Button onClick={() => handleSend(false)} disabled={busy || preview.problems?.length > 0}>
              Create links only
            </Button>
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => handleSend(true)}
              disabled={busy || preview.problems?.length > 0}
              sx={{ backgroundColor: navy, '&:hover': { backgroundColor: '#2E6DA4' } }}>
              {busy ? 'Working…' : 'Email for signature'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {detail && (
        <ContractDetail c={detail} narrow={narrow} defaults={defaults} onClose={() => setDetailId(null)}
          onEdit={() => openEdit(detail)} onDuplicate={() => duplicate(detail)}
          onMessage={setSuccess} onError={setError} />
      )}

      <Dialog open={defaultsOpen} onClose={() => setDefaultsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contract defaults</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            New contracts start from these. Changing them doesn’t touch existing contracts.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Season fee" type="number" value={defaultsForm.fee}
              onChange={e => setDefaultsForm(f => ({ ...f, fee: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Courts reserved" type="number" value={defaultsForm.courts}
              onChange={e => setDefaultsForm(f => ({ ...f, courts: e.target.value }))} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Window start" type="time" value={defaultsForm.windowStart}
              onChange={e => setDefaultsForm(f => ({ ...f, windowStart: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Window end" type="time" value={defaultsForm.windowEnd}
              onChange={e => setDefaultsForm(f => ({ ...f, windowEnd: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid size={{ xs: 12 }}>
              <DayPicker value={defaultsForm.days} onChange={days => setDefaultsForm(f => ({ ...f, days }))} />
            </Grid>
            {[['preseasonPrice', 'Pre-season'], ['districtPrice', 'District hosting'], ['regionalPrice', 'Regional hosting']].map(([k, label]) => (
              <Grid key={k} size={{ xs: 4 }}><TextField fullWidth size="small" label={label} type="number" value={defaultsForm[k]}
                onChange={e => setDefaultsForm(f => ({ ...f, [k]: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
            ))}
            <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="Checks payable to" value={defaultsForm.legalName}
              onChange={e => setDefaultsForm(f => ({ ...f, legalName: e.target.value }))} /></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="Check mailing address" value={defaultsForm.checkAddress}
              onChange={e => setDefaultsForm(f => ({ ...f, checkAddress: e.target.value }))} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Director name" value={defaultsForm.directorName}
              onChange={e => setDefaultsForm(f => ({ ...f, directorName: e.target.value }))} /></Grid>
            <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Director title" value={defaultsForm.directorTitle}
              onChange={e => setDefaultsForm(f => ({ ...f, directorTitle: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDefaultsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDefaults} sx={{ backgroundColor: navy }}>Save defaults</Button>
        </DialogActions>
      </Dialog>
    </Wrap>
  );
}

function DayPicker({ value = [], onChange }) {
  return (
    <ToggleButtonGroup size="small" value={value} onChange={(e, v) => onChange([...v].sort((a, b) => a - b))}>
      {DAY_LABELS.map((d, i) => <ToggleButton key={d} value={i} sx={{ px: 1.2 }}>{d}</ToggleButton>)}
    </ToggleButtonGroup>
  );
}

function EditorForm({ form, setForm, setAddOn, setSigner }) {
  const f = form;
  const money = { startAdornment: <InputAdornment position="start">$</InputAdornment> };
  const date = { shrink: true };
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>School</Typography>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="School name" value={f.schoolName}
          onChange={e => setForm({ schoolName: e.target.value })} /></Grid>
        <Grid size={{ xs: 12, sm: 8 }}><TextField fullWidth size="small" label="Address" value={f.schoolAddress}
          onChange={e => setForm({ schoolAddress: e.target.value })} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" label="Phone" value={f.schoolPhone}
          onChange={e => setForm({ schoolPhone: e.target.value })} /></Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2.5, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>School signers</Typography>
        <Button size="small" startIcon={<AddIcon />}
          onClick={() => setForm({ signers: [...f.signers, { name: '', title: '', email: '' }] })}>Add</Button>
      </Box>
      {f.signers.map((s, i) => (
        <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
          <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" label="Name" value={s.name}
            onChange={e => setSigner(i, { name: e.target.value })} /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" label="Title" value={s.title}
            onChange={e => setSigner(i, { title: e.target.value })} /></Grid>
          <Grid size={{ xs: 10, sm: 4 }}><TextField fullWidth size="small" label="Email" type="email" value={s.email}
            onChange={e => setSigner(i, { email: e.target.value })} /></Grid>
          <Grid size={{ xs: 2, sm: 1 }}>
            <IconButton size="small" onClick={() => setForm({ signers: f.signers.filter((_, j) => j !== i) })}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>Season and courts</Typography>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Season starts" type="date" value={f.seasonStart}
          onChange={e => setForm({ seasonStart: e.target.value })} InputLabelProps={date} /></Grid>
        <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Season ends" type="date" value={f.seasonEnd}
          onChange={e => setForm({ seasonEnd: e.target.value })} InputLabelProps={date} /></Grid>
        <Grid size={{ xs: 12 }}><DayPicker value={f.days} onChange={days => setForm({ days })} /></Grid>
        <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" label="Window start" type="time" value={f.windowStart}
          onChange={e => setForm({ windowStart: e.target.value })} InputLabelProps={date} /></Grid>
        <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" label="Window end" type="time" value={f.windowEnd}
          onChange={e => setForm({ windowEnd: e.target.value })} InputLabelProps={date} /></Grid>
        <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" label="Courts reserved" type="number" value={f.courts}
          onChange={e => setForm({ courts: Number(e.target.value) })} inputProps={{ min: 1, max: 6 }} /></Grid>
        <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" label="Season fee" type="number" value={f.fee}
          onChange={e => setForm({ fee: Number(e.target.value) })} InputProps={money} /></Grid>
        <Grid size={{ xs: 12, sm: 8 }}><TextField fullWidth size="small" label="Sport" value={f.sport}
          onChange={e => setForm({ sport: e.target.value })} /></Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5, mb: 0.5 }}>Add-ons</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <FormControlLabel sx={{ minWidth: 150 }} label="Pre-season access"
          control={<Checkbox checked={!!f.addOns.preseason.selected} onChange={e => setAddOn('preseason', { selected: e.target.checked })} />} />
        {f.addOns.preseason.selected && (
          <>
            <TextField size="small" type="date" label="From" value={f.addOns.preseason.start} InputLabelProps={date}
              onChange={e => setAddOn('preseason', { start: e.target.value })} />
            <TextField size="small" type="date" label="To" value={f.addOns.preseason.end} InputLabelProps={date}
              onChange={e => setAddOn('preseason', { end: e.target.value })} />
          </>
        )}
        <TextField size="small" type="number" label="Price" value={f.addOns.preseason.price} sx={{ width: 110 }}
          onChange={e => setAddOn('preseason', { price: Number(e.target.value) })} InputProps={money} />
      </Box>
      {[['district', 'District hosting'], ['regional', 'Regional hosting']].map(([k, label]) => (
        <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel sx={{ minWidth: 150 }} label={label}
            control={<Checkbox checked={!!f.addOns[k].selected} onChange={e => setAddOn(k, { selected: e.target.checked })} />} />
          <TextField size="small" type="number" label="Price" value={f.addOns[k].price} sx={{ width: 110 }}
            onChange={e => setAddOn(k, { price: Number(e.target.value) })} InputProps={money} />
        </Box>
      ))}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>Payment and extra terms</Typography>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 5 }}><TextField fullWidth size="small" label="Checks payable to" value={f.legalName}
          onChange={e => setForm({ legalName: e.target.value })} /></Grid>
        <Grid size={{ xs: 12, sm: 7 }}><TextField fullWidth size="small" label="Check mailing address" value={f.checkAddress}
          onChange={e => setForm({ checkAddress: e.target.value })} /></Grid>
        <Grid size={{ xs: 12 }}><TextField fullWidth size="small" multiline minRows={2} label="Additional terms (optional)"
          value={f.notes} onChange={e => setForm({ notes: e.target.value })} /></Grid>
      </Grid>
    </Box>
  );
}

function ContractDetail({ c, narrow, defaults, onClose, onEdit, onDuplicate, onMessage, onError }) {
  const [typedName, setTypedName] = useState(defaults.directorName || '');
  const [signature, setSignature] = useState(null);
  const [checkNo, setCheckNo] = useState('');
  const [checkAmount, setCheckAmount] = useState(String(totalCentsOf(c) / 100));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const anySigned = (c.signers || []).some(s => s.signedAt) || !!c.director?.signedAt;
  const paid = c.payment?.status === 'paid';
  const link = (s) => (s.token ? `${window.location.origin}/contract/${s.token}` : '');
  const toMs = (t) => (t?.toMillis ? t.toMillis() : null);
  const party = (s) => s && ({ ...s, signedAt: toMs(s.signedAt) });

  const run = async (fn, okMsg) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      if (okMsg) onMessage(okMsg);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const countersign = () => run(async () => {
    await httpsCallable(getFunctions(), 'countersignSchoolContract')({ contractId: c.id, typedName, signature });
  }, 'Countersigned.');

  const markCheck = () => run(async () => {
    const cents = Math.round(Number(checkAmount) * 100);
    if (!(cents > 0)) throw new Error('Enter the check amount.');
    await updateDoc(doc(db, 'schoolContracts', c.id), {
      payment: {
        status: cents >= totalCentsOf(c) ? 'paid' : 'partial', method: 'check', checkNumber: checkNo.trim(),
        amountCents: cents, paidAt: serverTimestamp(),
      },
      updatedAt: serverTimestamp(),
    });
  }, 'Check recorded.');

  // Before anyone signs, a sent contract can go back to being a draft. The
  // links die with the seal, so the school can't sign the old wording.
  const backToDraft = () => run(async () => {
    await updateDoc(doc(db, 'schoolContracts', c.id), {
      status: 'draft', html: deleteField(), docHash: deleteField(), tokenHashes: [],
      signers: (c.signers || []).map(s => ({ name: s.name, title: s.title || '', email: s.email })),
      updatedAt: serverTimestamp(),
    });
    onEdit();
  });

  const withdraw = () => run(async () => {
    await updateDoc(doc(db, 'schoolContracts', c.id), { status: 'void', tokenHashes: [], updatedAt: serverTimestamp() });
    onClose();
  }, 'Contract withdrawn. Its links no longer open.');

  const remove = () => run(async () => {
    await deleteDoc(doc(db, 'schoolContracts', c.id));
    onClose();
  }, 'Deleted.');

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      onMessage('Link copied.');
    } catch {
      setError(text);
    }
  };

  return (
    <Dialog open fullScreen={narrow} maxWidth="lg" fullWidth onClose={() => !busy && onClose()}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {c.schoolName}
        <Chip size="small" color={(STATUS[c.status] || STATUS.draft).color} label={(STATUS[c.status] || STATUS.draft).label} />
        <Chip size="small" variant="outlined" color={PAYMENT[c.payment?.status || 'unpaid'].color}
          label={`${PAYMENT[c.payment?.status || 'unpaid'].label}${c.payment?.checkNumber ? ` · check ${c.payment.checkNumber}` : ''}`} />
        <Box sx={{ flex: 1 }} />
        {/* Printing from inside a dialog clips to the dialog; the signing page
            prints the agreement cleanly, so open that. */}
        {c.signers?.[0]?.token && c.status !== 'void' && (
          <Button size="small" startIcon={<PrintIcon />} href={link(c.signers[0])} target="_blank" rel="noopener">
            Open printable copy
          </Button>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Signatures</Typography>
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            {(c.signers || []).map((s, i) => (
              <TableRow key={i}>
                <TableCell>{s.name}<Typography variant="caption" sx={{ display: 'block', color: '#888' }}>{s.title} · {s.email}</Typography></TableCell>
                <TableCell>
                  {s.signedAt
                    ? <Chip size="small" color="success" label={`Signed ${new Date(toMs(s.signedAt)).toLocaleString()}`} />
                    : <Chip size="small" label="Waiting" />}
                  {s.ip && <Typography variant="caption" sx={{ display: 'block', color: '#999' }}>from {s.ip}</Typography>}
                </TableCell>
                <TableCell align="right">
                  {s.token && c.status !== 'void' && (
                    <Tooltip title="Copy this signer’s link">
                      <IconButton size="small" onClick={() => copy(link(s))}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>{defaults.directorName}<Typography variant="caption" sx={{ display: 'block', color: '#888' }}>{defaults.directorTitle}</Typography></TableCell>
              <TableCell>
                {c.director?.signedAt
                  ? <Chip size="small" color="success" label={`Signed ${new Date(toMs(c.director.signedAt)).toLocaleString()}`} />
                  : <Chip size="small" label="Countersign below" />}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>

        {c.status === 'sent' && !c.director?.signedAt && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Countersign as director</Typography>
            <TextField fullWidth size="small" label="Type your full name" value={typedName}
              onChange={e => setTypedName(e.target.value)} sx={{ mb: 1.5 }} />
            <SignaturePad onChange={setSignature} disabled={busy} height={140} />
            <Button variant="contained" sx={{ mt: 1, backgroundColor: navy }} onClick={countersign}
              disabled={busy || !signature || typedName.trim().length < 2}>Countersign</Button>
          </Paper>
        )}

        {!paid && c.status !== 'void' && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Record a check · total {fmtMoney(totalCentsOf(c))}
              {c.payment?.status === 'partial' && ` · ${fmtMoney(c.payment.amountCents || 0)} received so far`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField size="small" label="Check number" value={checkNo} onChange={e => setCheckNo(e.target.value)} />
              <TextField size="small" label="Amount" type="number" value={checkAmount} sx={{ width: 140 }}
                onChange={e => setCheckAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
              <Button variant="outlined" onClick={markCheck} disabled={busy}>Mark paid by check</Button>
            </Box>
          </Paper>
        )}

        <Divider sx={{ my: 2 }} />
        <SchoolContractDocument html={c.html} docHash={c.docHash} schoolName={c.schoolName}
          signers={(c.signers || []).map(party)} director={party(c.director) || { name: defaults.directorName, title: defaults.directorTitle }} />
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {c.status === 'draft' && <Button color="error" onClick={remove} disabled={busy}>Delete draft</Button>}
        {c.status === 'sent' && !anySigned && <Button onClick={backToDraft} disabled={busy}>Back to draft to edit</Button>}
        {c.status !== 'void' && c.status !== 'draft' && !paid && (
          <Button color="error" onClick={withdraw} disabled={busy}>Withdraw</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onDuplicate} disabled={busy}>Duplicate for next season</Button>
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
